import { AppDispatch } from '@/app/store';
import { AuthorDTO } from '@/types/AuthorDTO';
import { AutoPrintEnabledInfoDialog } from '@/components/create/AutoPrintEnabledInfoDialog';
import { Button, Collapse, Grid, Theme, Typography } from '@mui/material';
import { CreateFormAnswerProps } from '@/types/create/CreateFormAnswerProps';
import { CreateLetterDTO } from '@/types/create/CreateLetterDTO';
import { DepartmentDTO } from '@/types/DepartmentDTO';
import { DownIcon } from '@/app/icons/DownIcon';
import { Footer } from '@/components/main/footer/Footer';
import { LetterAlert } from '@/components/create/letter/LetterAlert';
import { LetterBalance } from '@/components/create/unauthorized/LetterBalance';
import { LetterInfo } from '@/components/create/LetterInfo';
import { LetterReceipt } from '@/components/common/LetterReceipt';
import { LetterStatus } from '@/types/letter/LetterStatus';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/common/PageHeader';
import { PaymentMethod } from '@/types/payment/PaymentMethod';
import { PaymentProvider } from '@/types/payment/PaymentProvider';
import { PhotoPanelGrid } from '@/components/create/PhotoPanelGrid';
import { RegionDTO } from '@/types/RegionDTO';
import { SenderDTO } from '@/types/SenderDTO';
import { StagedPhotoDTO } from '@/types/create/StagedPhotoDTO';
import { SudokuDifficulty } from '@/types/create/SudokuDifficulty';
import { SudokuType } from '@/types/create/SudokuType';
import { TypographyLink } from '@/components/common/TypographyLink';
import { UpIcon } from '@/app/icons/UpIcon';
import { UserDTO } from '@/types/user/UserDTO';
import { calculatePageCount, calculateTotalCost } from '@/utils/price-utils';
import {
  clearLetterTextAndFile,
  getAnswerOrder,
  getAnswersCount,
  getAuthor,
  getCrosswordAttached,
  getCrosswordCount,
  getDepartment,
  getIdempotencyKey,
  getLetterNumber,
  getLetterSent,
  getLetterText,
  getLetterUuid,
  getNewsAttached,
  getPaymentMethod,
  getPaymentProvider,
  getRegion,
  getSender,
  getSportsNewsAttached,
  getStagedPhotos,
  getSudokuAttached,
  getSudokuCount,
  getSudokuDifficulty,
  getSudokuType,
  setAuthor,
} from '@/services/createFormSlice';
import { formatLetterNumberDisplay, formatUserFullName } from '@/utils/string-utils';
import { getCrosswordCost, getLetterCost, getNewsCost, getPhotoCost, getSudokuCost } from '@/utils/currency-utils';
import { getCurrentUser } from '@/services/authSlice';
import { letterApiSlice, useGetLetterByUuidQuery } from '@/services/api/letterApiSlice';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useGetClientInfoQuery } from '@/services/api/createFormApiSlice';
import { useGetCurrentUserQuery } from '@/services/api/authApiSlice';
import { useStagedPhotoExpiry } from '@/app/hooks/useStagedPhotoExpiry';
import { validateAuthorFull } from '@/utils/validation-utils';
import AttachFilePanel from '@/components/create/AttachFilePanel';
import LetterCostPanel from '@/components/create/LetterCostPanel';
import React, { FC, ReactElement, useEffect, useState } from 'react';

interface Props {
  isAnswerReply?: boolean;
}

export const MainCreateFormPaymentMobile: FC<CreateFormAnswerProps & Props> = (
  props: CreateFormAnswerProps & Props,
): ReactElement => {
  const {
    previousFormPath,
    previousFormLabel,
    errorRedirectPath,
    nextFormPath,
    isMobile,
    mobileTittle,
    fromCreateAnswer,
    isAnswerReply,
  } = props;

  const dispatch: AppDispatch = useAppDispatch();
  const navigate: NavigateFunction = useNavigate();

  const authUser: UserDTO | undefined = useAppSelector(getCurrentUser);
  const department: DepartmentDTO | null = useAppSelector(getDepartment);
  const region: RegionDTO | null = useAppSelector(getRegion);
  const letterText: string | undefined = useAppSelector(getLetterText);
  const isAnswerOrdered: boolean = useAppSelector(getAnswerOrder);
  const sender: SenderDTO | null = useAppSelector(getSender);
  const author: AuthorDTO | null = useAppSelector(getAuthor);
  const answersCount: number = useAppSelector(getAnswersCount);
  const stagedPhotos: StagedPhotoDTO[] = useAppSelector(getStagedPhotos);
  const sudokuAttached: boolean = useAppSelector(getSudokuAttached);
  const sudokuCount: number = useAppSelector(getSudokuCount);
  const sudokuType: SudokuType = useAppSelector(getSudokuType);
  const sudokuDifficulty: SudokuDifficulty = useAppSelector(getSudokuDifficulty);
  const crosswordAttached: boolean = useAppSelector(getCrosswordAttached);
  const crosswordCount: number = useAppSelector(getCrosswordCount);
  const newsAttached: boolean = useAppSelector(getNewsAttached);
  const sportsNewsAttached: boolean = useAppSelector(getSportsNewsAttached);
  const isLetterSent: boolean = useAppSelector(getLetterSent);
  const idempotencyKey: string | undefined = useAppSelector(getIdempotencyKey);
  const letterNumber: number | undefined = useAppSelector(getLetterNumber);
  const letterUuid: string | undefined = useAppSelector(getLetterUuid);
  const paymentMethod: PaymentMethod = useAppSelector(getPaymentMethod);
  const paymentProvider: PaymentProvider | undefined = useAppSelector(getPaymentProvider);

  // Once the letter has been created the photos are a receipt rather than a form, so they are left
  // alone however old they are.
  useStagedPhotoExpiry(!isLetterSent);

  useEffect((): void => {
    if (!(department && region && sender && author)) {
      if (errorRedirectPath) {
        navigate(errorRedirectPath);
      }
    }
  }, [author, department, errorRedirectPath, navigate, region, sender]);

  const photoCost: number = getPhotoCost(!!authUser, department);
  const sudokuCost: number = getSudokuCost(!!authUser, department);
  const crosswordCost: number = getCrosswordCost(!!authUser, department);
  const newsCost: number = getNewsCost(!!authUser, department);
  const letterCost: number = getLetterCost(!!authUser, department);
  const authorizedLetterCost: number = getLetterCost(true, department);
  const authorizedPhotoCost: number = getPhotoCost(true, department);
  const authorizedSudokuCost: number = getSudokuCost(true, department);
  const authorizedCrosswordCost: number = getCrosswordCost(true, department);
  const authorizedNewsCost: number = getNewsCost(true, department);
  const countCharacters: number | undefined = letterText?.length;

  const { data: user } = useGetCurrentUserQuery(authUser?.id, {
    skip: !authUser,
  });
  const { data: letter } = useGetLetterByUuidQuery(letterUuid, {
    skip: !letterUuid,
    refetchOnMountOrArgChange: true,
  });

  const { data: clientInfo } = useGetClientInfoQuery();
  const charactersPerPage: number | undefined = clientInfo?.charactersPerPage;

  const dto: CreateLetterDTO = {
    department: department?.id,
    author: author,
    sender: sender,
    letter: letterText,
    isAnswerOrdered: isAnswerOrdered,
    answersCount: answersCount,
    sudokuAttached: sudokuAttached,
    sudokuCount: sudokuCount,
    sudokuType: sudokuType,
    sudokuDifficulty: sudokuDifficulty,
    crosswordAttached: crosswordAttached,
    crosswordCount: crosswordCount,
    newsAttached: newsAttached,
    sportsNewsAttached: sportsNewsAttached,
    photoIds: stagedPhotos.map((photo: StagedPhotoDTO): string => photo.id),
  };

  const pageCount: number | undefined = calculatePageCount(countCharacters || 0, charactersPerPage || 0);

  const totalCost: number = calculateTotalCost(
    pageCount,
    isAnswerOrdered,
    answersCount,
    stagedPhotos.length,
    crosswordAttached,
    crosswordCount,
    sudokuAttached,
    sudokuCount,
    newsAttached,
    sportsNewsAttached,
    letterCost,
    letterCost,
    photoCost,
    sudokuCost,
    crosswordCost,
    newsCost,
  );

  const authorizedTotalCost: number = calculateTotalCost(
    pageCount,
    isAnswerOrdered,
    answersCount,
    stagedPhotos.length,
    crosswordAttached,
    crosswordCount,
    sudokuAttached,
    sudokuCount,
    newsAttached,
    sportsNewsAttached,
    authorizedLetterCost,
    authorizedLetterCost,
    authorizedPhotoCost,
    authorizedSudokuCost,
    authorizedCrosswordCost,
    authorizedNewsCost,
  );

  const [collapse, setCollapse] = useState<boolean>(false);
  const [openAutoPrintInfoDialog, setOpenAutoPrintInfoDialog] = useState(false);

  const toChangeAnswerReply = (): void => {
    if (previousFormPath) {
      navigate(previousFormPath);
    }
  };

  useEffect((): void => {
    if (fromCreateAnswer) {
      dispatch(clearLetterTextAndFile());
    }
  }, [dispatch, fromCreateAnswer]);

  useEffect((): (() => void) => {
    if (!!letter && letter?.status !== LetterStatus.UNPAID) {
      return (): void => {};
    }
    const timerId: ReturnType<typeof setInterval> = setInterval((): void => {
      dispatch(letterApiSlice.endpoints.getLetterByUuid.initiate(letterUuid, { forceRefetch: true }));
    }, 30000);
    return (): void => {
      clearInterval(timerId);
    };
  }, [dispatch, letter, letterUuid]);

  useEffect((): void => {
    if (user) {
      if (!validateAuthorFull(author)) {
        const updatedAuthor: AuthorDTO = {
          name: author?.name || formatUserFullName(user),
          phone: author?.phone || user?.phone || '',
          email: author?.email || user?.email,
        };
        const hasSameData: boolean =
          author?.name === updatedAuthor.name &&
          author?.phone === updatedAuthor.phone &&
          author?.email === updatedAuthor.email;
        if (!hasSameData) {
          dispatch(setAuthor(updatedAuthor));
        }
      }
    }
  }, [author, dispatch, user]);

  const isPaymentSuccess: boolean = !!letter && letter?.status !== LetterStatus.UNPAID;

  return (
    <>
      <Grid container={true} direction={'column'} spacing={3}>
        <Grid item={true}>
          <Grid container={true} direction={'column'} spacing={2}>
            <Grid item={true}>
              {!isLetterSent ? (
                <PageHeader
                  tittle={mobileTittle}
                  previousFormPath={previousFormPath}
                  previousFormLabel={previousFormLabel}
                  isMobile={isMobile}
                  mobileTittle={mobileTittle}
                />
              ) : fromCreateAnswer ? (
                <PageHeader
                  tittle={mobileTittle}
                  isMobile={isMobile}
                  mobileTittle={
                    isPaymentSuccess
                      ? `Бланки ответа ${letterNumber && '№ '}${formatLetterNumberDisplay(letterNumber) || ''} оплачены`
                      : `Ожидается оплата бланков ответа ${letterNumber && '№ '}${
                          formatLetterNumberDisplay(letterNumber) || ''
                        }`
                  }
                />
              ) : (
                <PageHeader
                  tittle={mobileTittle}
                  isMobile={isMobile}
                  mobileTittle={
                    isPaymentSuccess
                      ? `Письмо ${letterNumber && '№ '}${
                          formatLetterNumberDisplay(letterNumber) || ''
                        } принято к доставке`
                      : `Ожидается оплата письма ${letterNumber && '№ '}${
                          formatLetterNumberDisplay(letterNumber) || ''
                        }`
                  }
                />
              )}
            </Grid>
            {department?.autoPrintEnabled && (
              <Grid item={true}>
                <LetterAlert isWarn={true}>
                  <Typography variant={'body1'}>
                    {'Действуют особые условия доставки. '}
                    <TypographyLink onClick={() => setOpenAutoPrintInfoDialog(true)}>{'Подробнее'}</TypographyLink>
                  </Typography>
                </LetterAlert>
              </Grid>
            )}
            {fromCreateAnswer ? (
              <Grid item={true}>
                <Grid container={true} direction={'column'} spacing={2}>
                  <Grid item={true}>
                    <LetterInfo
                      department={department}
                      region={region}
                      author={author}
                      sender={sender}
                      letterText={letterText}
                      isMobile={isMobile}
                      fromCreateAnswer={fromCreateAnswer}
                      isAnswerReply={isAnswerReply}
                      changeAnswerReply={toChangeAnswerReply}
                      answersCount={answersCount}
                    />
                  </Grid>
                  {isLetterSent && (
                    <Grid item={true}>
                      <LetterCostPanel allPrice={totalCost} fromCreateAnswer={fromCreateAnswer} />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            ) : (
              <Grid item={true}>
                <Grid container={true} direction={'column'} spacing={3}>
                  <Grid item={true} xs={true}>
                    <Button
                      onClick={() => setCollapse(!collapse)}
                      fullWidth={true}
                      variant={'contained'}
                      color={'inherit'}
                      sx={{
                        background: (theme: Theme) => theme.colors.grayBackground,
                        color: (theme: Theme) => theme.colors.textColor,
                      }}>
                      <Grid container={true} justifyContent={'space-between'}>
                        <Grid item={true}>{'Информация о письме'}</Grid>
                        <Grid item={true}>
                          {collapse ? (
                            <UpIcon
                              sx={{
                                display: 'block',
                                color: (theme: Theme) => theme.colors.primary,
                              }}
                            />
                          ) : (
                            <DownIcon
                              sx={{
                                display: 'block',
                                color: (theme: Theme) => theme.colors.primary,
                              }}
                            />
                          )}
                        </Grid>
                      </Grid>
                    </Button>
                  </Grid>
                  <Grid item={true}>
                    <Collapse in={collapse}>
                      <Grid container={true} direction={'column'} rowSpacing={2}>
                        <Grid item={true}>
                          <LetterInfo
                            department={department}
                            region={region}
                            author={author}
                            sender={sender}
                            letterText={letterText}
                            isMobile={isMobile}
                            fromCreateAnswer={fromCreateAnswer}
                            isAnswerReply={isAnswerReply}
                            changeAnswerReply={toChangeAnswerReply}
                            answersCount={answersCount}
                          />
                        </Grid>
                        {!!photoCost && stagedPhotos.length > 0 && (
                          <Grid item={true}>
                            <PhotoPanelGrid>
                              {stagedPhotos.map(
                                (photo: StagedPhotoDTO, index: number): ReactElement => (
                                  <AttachFilePanel
                                    key={photo.id}
                                    thumbnail={photo.thumbnail}
                                    photoCost={photoCost}
                                    fullWidth={true}
                                    label={stagedPhotos.length > 1 ? `Фото ${index + 1}` : undefined}
                                  />
                                ),
                              )}
                            </PhotoPanelGrid>
                          </Grid>
                        )}
                        {isLetterSent && (
                          <Grid item={true}>
                            <LetterCostPanel
                              allPrice={totalCost}
                              countCharacters={countCharacters}
                              pages={pageCount}
                              photoCount={photoCost ? stagedPhotos.length : 0}
                              answer={isAnswerOrdered}
                              fromCreateAnswer={fromCreateAnswer}
                            />
                          </Grid>
                        )}
                      </Grid>
                    </Collapse>
                  </Grid>
                  <Grid item={true}>
                    <LetterReceipt
                      countCharacters={countCharacters}
                      photoCount={stagedPhotos.length}
                      sudokuAttached={sudokuAttached}
                      sudokuCount={sudokuCount}
                      crosswordAttached={crosswordAttached}
                      crosswordCount={crosswordCount}
                      newsAttached={newsAttached}
                      sportsNewsAttached={sportsNewsAttached}
                      pages={pageCount}
                      letterCost={letterCost}
                      authorizedLetterCost={authorizedLetterCost}
                      photoCost={photoCost}
                      authorizedPhotoCost={authorizedPhotoCost}
                      sudokuCost={sudokuCost}
                      authorizedSudokuCost={authorizedSudokuCost}
                      crosswordCost={crosswordCost}
                      authorizedCrosswordCost={authorizedCrosswordCost}
                      newsCost={newsCost}
                      authorizedNewsCost={authorizedNewsCost}
                      totalCost={totalCost}
                      authorizedTotalCost={authorizedTotalCost}
                      isAnswerOrdered={isAnswerOrdered || false}
                      answersCount={answersCount}
                      paymentMethod={paymentMethod}
                      paymentProvider={paymentProvider}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid item={true}>
          <LetterBalance
            photoCount={stagedPhotos.length}
            sudokuAttached={sudokuAttached}
            sudokuCount={sudokuCount}
            crosswordAttached={crosswordAttached}
            crosswordCount={crosswordCount}
            newsAttached={newsAttached}
            sportsNewsAttached={sportsNewsAttached}
            isAnswerOrdered={isAnswerOrdered}
            letterCost={letterCost}
            authorizedLetterCost={authorizedLetterCost}
            photoCost={photoCost}
            authorizedPhotoCost={authorizedPhotoCost}
            sudokuCost={sudokuCost}
            authorizedSudokuCost={authorizedSudokuCost}
            crosswordCost={crosswordCost}
            authorizedCrosswordCost={authorizedCrosswordCost}
            newsCost={newsCost}
            authorizedNewsCost={authorizedNewsCost}
            totalCost={totalCost}
            authorizedTotalCost={authorizedTotalCost}
            afterSuccessPath={nextFormPath}
            letter={dto}
            isLetterSent={isLetterSent}
            isMobile={true}
            pages={pageCount}
            countCharacters={countCharacters}
            fromCreateAnswer={fromCreateAnswer}
            answersCount={answersCount}
            idempotencyKey={idempotencyKey}
            isPaymentSuccess={isPaymentSuccess}
          />
        </Grid>
        {isLetterSent && (
          <Grid item={true}>
            <Footer isMobile={true} />
          </Grid>
        )}
      </Grid>
      <AutoPrintEnabledInfoDialog
        handleCloseButton={() => setOpenAutoPrintInfoDialog(false)}
        open={openAutoPrintInfoDialog}
        isMobile={true}
      />
    </>
  );
};
